import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { DialogModalDS, ButtonDS, Spacer, TextCapitalized } from "@qivia/ui";
import styled from "styled-components";
import { SepaDirectDebitReturnedDisplayed } from "../sepaDirectDebitApi";
import { updateReturnedRefundDateAsync } from "../sepaDirectDebitSlice";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { selectAdminUser } from "../../homeSlice";

interface UpdateReturnedRefundDateModalProps {
  onClose: () => void;
  selectedRow: SepaDirectDebitReturnedDisplayed | null;
}

export const UpdateReturnedRefundDateModal = (
  props: UpdateReturnedRefundDateModalProps,
) => {
  const { onClose, selectedRow } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const adminUser = useAppSelector(selectAdminUser);
  const [newRefundDate, setNewRefundDate] = useState<string>("");

  const handleConfirm = useCallback(() => {
    if (selectedRow && newRefundDate && adminUser) {
      void dispatch(
        updateReturnedRefundDateAsync({
          sepaDirectDebitUuid: selectedRow.uuid,
          returnedRefundDate: new Date(newRefundDate).toISOString(),
          updatedBy: adminUser.email,
        }),
      );
      onClose();
      setNewRefundDate("");
    }
  }, [selectedRow, newRefundDate, onClose, dispatch, adminUser]);

  const handleClose = useCallback(() => {
    onClose();
    setNewRefundDate("");
  }, [onClose]);

  return (
    <DialogModalDS
      visible={selectedRow !== null}
      onClose={handleClose}
      title={t("sepaDirectDebitReturned.modal.title")}
      width={45}
      bottomButtons={{
        rightButton: (
          <ButtonDS
            text={t("sepaDirectDebitReturned.modal.confirm")}
            format="fill"
            buttonType="primary"
            disabled={!newRefundDate}
            onClick={handleConfirm}
          />
        ),
        leftButton: (
          <ButtonDS
            text={t("sepaDirectDebitReturned.modal.cancel")}
            format="fill"
            buttonType="secondary"
            onClick={handleClose}
          />
        ),
      }}
    >
      <StyledModalContent>
        <TextCapitalized>
          {t("sepaDirectDebitReturned.modal.description", {
            companyName: selectedRow?.companyName,
            amount: selectedRow?.amount,
          })}
        </TextCapitalized>
        <Spacer y={2} />
        <TextCapitalized>
          {t("sepaDirectDebitReturned.modal.dateLabel")}
        </TextCapitalized>
        <Spacer y={2} />
        <input
          type="date"
          value={newRefundDate}
          max={new Date().toISOString().split("T")[0]}
          onChange={(e) => setNewRefundDate(e.target.value)}
        />
        <Spacer y={2} />
      </StyledModalContent>
    </DialogModalDS>
  );
};

const StyledModalContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
